import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import { Box, Paper, Typography, Grid } from '@material-ui/core';
import { numberInCommaFormat, getPartner } from '../../../../../../lib/access.es6';
import ButtonInc from '../../../../../../shared-ui/ButtonInc';
import { getAdditionalBenefits, group_life_options } from '../../../../PolicyAdmin/helpers';
import {
  getMemberBenefitsWithState,
  calculateSelectedPremium,
  hasChanges,
  isCoreBenefit
} from '../utils/benefitHelpers';

export default function ManageBenefitsModal(props) {
  const { open, member, policy, products, onSave, onClose } = props;
  const [benefits, setBenefits] = useState([]);
  const [originalBenefits, setOriginalBenefits] = useState([]);

  useEffect(() => {
    if (!open || !member) return;

    // Find the plan from products array using the policy's product_guid
    // The plan contains membership_configs with premium waivers, annuities, and add-ons
    const plan = products && products.length > 0 && policy.product_guid
      ? products.find(p => p.guid === policy.product_guid)
      : null;

    // Get the cover type from the policy's quotation
    // Cover type can be "Member Only" or "Member & Family"
    const coverType = policy && policy.quotation && policy.quotation.additional_details
      ? policy.quotation.additional_details.cover_type
      : null;

    // Determine what to pass to getAdditionalBenefits
    // For nuclear members (Principal, Spouse, Child): use coverage type
    // For parent/extended members: use member relationship (matches create quote pattern)
    const isNuclearMember = ['Principal', 'Spouse', 'Child'].includes(member.relationship);
    const filterValue = isNuclearMember ? coverType : member.relationship;

    // Get available additional benefits
    let availableBenefits = getAdditionalBenefits(filterValue, plan);

    // Add Group Life options for Principal and Spouse
    if (member.relationship === 'Principal') {
      availableBenefits = [...availableBenefits, ...group_life_options];
    }
    if (member.relationship === 'Spouse') {
      availableBenefits = [...availableBenefits, ...group_life_options];
    }

    // Get current member benefits (includes both core and additional)
    const currentBenefits = member.benefits || [];

    // Filter out core benefits (Funeral, Accidental Death) - these come with the plan
    // and should not be shown in the additional benefits dialog
    const currentAdditionalBenefits = currentBenefits.filter(b => !isCoreBenefit(b));

    // Merge with available benefits to show selection state
    const benefitsWithState = getMemberBenefitsWithState(
      { benefits: currentAdditionalBenefits },
      availableBenefits
    );

    setBenefits(benefitsWithState);
    setOriginalBenefits(currentBenefits);
  }, [open, member, policy, products]);

  const handleSelect = (benefit) => {
    const newBenefits = benefits.map(item => {
      if (item.uuid === benefit.uuid) {
        return { ...item, selected: !item.selected };
      }
      return item;
    });
    setBenefits(newBenefits);
  };

  const handleSave = () => {
    // Check if changes were made
    if (!hasChanges(originalBenefits, benefits)) {
      alert('No changes were made to benefits');
      return;
    }

    // Get selected additional benefits
    const selectedAdditionalBenefits = benefits.filter(b => b.selected);

    // Get core benefits from original (these should always remain)
    const coreBenefits = originalBenefits.filter(b => isCoreBenefit(b));

    // Combine core benefits with selected additional benefits
    const allBenefits = [...coreBenefits, ...selectedAdditionalBenefits];

    onSave(member, allBenefits);
  };

  if (!open || !member) return null;

  const currentPremium = calculateSelectedPremium(benefits);
  const currencyCode = getPartner().country.currency_code;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="manage-benefits-dialog"
      maxWidth="lg"
      fullWidth
    >
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h4" gutterBottom>
              Manage Additional Benefits for {member.first_name} {member.last_name}
            </Typography>
            <Typography variant="h6" gutterBottom>
              Click to add or remove optional benefits (Premium Waivers, Annuities, Add-ons, Group Life)
            </Typography>
            <Typography variant="body2" style={{ marginTop: '5px', color: '#666' }}>
              Note: Core benefits (Funeral & Accidental Death) are included with the plan and cannot be removed.
            </Typography>
            <Typography variant="body1" style={{ marginTop: '10px', fontWeight: 'bold' }}>
              Additional Benefits Premium: {currencyCode} {numberInCommaFormat(currentPremium / 100)}
            </Typography>
            <br />
          </Grid>

          {benefits.map((benefit) => (
            <Grid item xs={12} sm={6} md={4} key={benefit.uuid}>
              <Box
                component={Paper}
                p={2}
                onClick={() => handleSelect(benefit)}
                style={{
                  backgroundColor: benefit.selected ? '#032A37' : '#fff',
                  color: benefit.selected ? '#fff' : '#000',
                  cursor: 'pointer'
                }}
              >
                <Typography variant="h6">
                  {benefit.name}
                </Typography>
                {benefit.premium_amount && (
                  <Typography variant="body1">
                    Premium: {currencyCode} {numberInCommaFormat(benefit.premium_amount / 100)}
                  </Typography>
                )}
                {benefit.benefit_amount && (
                  <Typography variant="body1">
                    Benefit: {currencyCode} {numberInCommaFormat(benefit.benefit_amount / 100)}
                  </Typography>
                )}
                {benefit.benefit_amount_in_cents && (
                  <Typography variant="body1">
                    Benefit: {currencyCode} {numberInCommaFormat(benefit.benefit_amount_in_cents / 100)}
                  </Typography>
                )}
                {benefit.name && benefit.name.includes('Waiver') && (
                  <Typography variant="body1">
                    Benefit: {benefit.name}
                  </Typography>
                )}
              </Box>
            </Grid>
          ))}
          <Grid item xs={12}></Grid>
        </Grid>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '15px',
          marginTop: '30px',
          paddingTop: '20px',
          borderTop: '1px solid #e0e0e0'
        }}>
          <ButtonInc onClick={onClose} variant="outlined" style={{ minWidth: '120px' }}>
            Cancel
          </ButtonInc>
          <ButtonInc onClick={handleSave} style={{ minWidth: '120px', background: '#032A37' }}>
            Save Changes
          </ButtonInc>
        </div>
      </DialogContent>
    </Dialog>
  );
}
